// Keyboard shortcuts — the central registry. core.md §7.0 / §7.5.
//
// Everything registers HERE, once, through `useShortcut`. Screens never attach
// their own `window.addEventListener` for a key: two screens binding the same
// key independently is invisible until they are mounted together, and a key
// nobody can enumerate is a key nobody discovers — the `?` overlay can only list
// what the registry knows.
//
// Global bindings (App.jsx): `Ctrl/⌘+K` focuses the patient search on
// `/patients`, `?` opens the help overlay, `Esc` closes any sheet. The visit
// workspace registers its own (`Ctrl+S`, `Ctrl+1..4`, `Ctrl+Enter`,
// `Ctrl+Shift+E`) while it is mounted, and hands them back when it unmounts.
//
// Precedence is last-registered-wins, so a modal that registers `esc` shadows
// the screen underneath it for as long as it is mounted.
//
// This file is pure React (no JSX) so it stays a `.js`; the overlay that lists
// the bindings is `src/components/common/ShortcutHelp.jsx`.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

const RegistryCtx = createContext(null);
const HelpCtx = createContext(null);

let SEQ = 0;
const FKEY = /^f([1-9]|1[0-2])$/;

/** Canonical form of a combo: modifiers in a fixed order, lowercase, `esc`. */
export const normalizeCombo = (raw) => {
  const parts = String(raw || '').toLowerCase().split('+').map((s) => s.trim()).filter(Boolean);
  let key = '';
  const mods = new Set();
  for (const p of parts) {
    if (p === 'ctrl' || p === 'cmd' || p === 'meta') mods.add('ctrl');
    else if (p === 'alt' || p === 'option') mods.add('alt');
    else if (p === 'shift') mods.add('shift');
    else key = p === 'escape' ? 'esc' : p;
  }
  if (!key) return '';
  const out = [];
  if (mods.has('ctrl')) out.push('ctrl');
  if (mods.has('alt')) out.push('alt');
  if (mods.has('shift')) out.push('shift');
  out.push(key);
  return out.join('+');
};

const LETTER = /^[a-z]$/;
const CODE_LETTER = /^Key([A-Z])$/;
const CODE_DIGIT = /^Digit([0-9])$/;

/**
 * The combo a KeyboardEvent represents. Meta folds into ctrl so a Mac gets ⌘K
 * for free.
 *
 * ── Shift joins the combo for LETTERS and for named keys, never for symbols ──
 *
 * `?` is Shift+/ on most layouts: the event already says `?`, so a `shift+`
 * prefix would make the help key unmatchable. A letter is the opposite case —
 * Shift+E reports `E`, which lowercases to the same `e` as a plain press, so
 * the ONLY thing that tells `Ctrl+Shift+E` (end the visit) from `Ctrl+E` is the
 * modifier, and it has to be in the combo. Nothing registers a bare `shift+e`,
 * so typing a capital letter in a field still matches nothing.
 *
 * ── A modified key is read from `e.code` when `e.key` is not ASCII ─────────
 *
 * On a Persian layout `Ctrl+S` reports `key: 'س'`; the physical key is still
 * `code: 'KeyS'`. Only while Ctrl/Alt are held, and only for a non-ASCII key,
 * so plain typing is never remapped and `Ctrl+Shift+1` (`!` on a US layout)
 * stays the unbound `ctrl+!` it always was.
 */
export const comboFromEvent = (e) => {
  let key = e?.key;
  if (!key) return '';
  key = key === 'Escape' ? 'esc' : key === ' ' ? 'space' : key.toLowerCase();
  const modified = e.ctrlKey || e.metaKey || e.altKey;
  if (modified && key.length === 1 && key.charCodeAt(0) > 127) {
    const letter = CODE_LETTER.exec(e.code || '');
    const digit = CODE_DIGIT.exec(e.code || '');
    if (letter) key = letter[1].toLowerCase();
    else if (digit) key = digit[1];
  }
  const out = [];
  if (e.ctrlKey || e.metaKey) out.push('ctrl');
  if (e.altKey) out.push('alt');
  if (e.shiftKey && (key.length > 1 || LETTER.test(key))) out.push('shift');
  out.push(key);
  return out.join('+');
};

/** True while the caret is somewhere typing happens. */
export const isEditableTarget = (el) => {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
};

/** Function keys, Escape and anything with a modifier fire inside a field; a bare `?` does not. */
export const firesInInputByDefault = (combo) => {
  const key = combo.split('+').pop();
  return combo.includes('ctrl+') || combo.includes('alt+') || key === 'esc' || FKEY.test(key);
};

const CAP = { ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift', esc: 'Esc', space: 'Space', enter: 'Enter' };
export const comboLabel = (combo) =>
  combo.split('+').map((p) => CAP[p] || p.toUpperCase()).join(' + ');

export function ShortcutProvider({ children }) {
  // A ref, not state: registering must not re-render the tree that is registering.
  const regs = useRef(new Map());
  const [revision, bump] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const register = useCallback((id, def) => {
    regs.current.set(id, def);
    bump((v) => v + 1);
    return () => { regs.current.delete(id); bump((v) => v + 1); };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKey = (e) => {
      const combo = comboFromEvent(e);
      if (!combo) return;
      const editable = isEditableTarget(e.target);
      let best = null;
      for (const def of regs.current.values()) {
        if (def.combo !== combo) continue;
        const fires = def.firesInInput ?? firesInInputByDefault(def.combo);
        if (editable && !fires) continue;
        if (!best || def.seq > best.seq) best = def;
      }
      if (!best) return;
      // Claim the key only once something handles it, so an unbound F5 still reloads.
      e.preventDefault();
      e.stopPropagation();
      best.run(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Stable for the provider's lifetime — `useShortcut` lists it as a dependency.
  const registry = useMemo(() => ({ register, regs }), [register]);
  const help = useMemo(() => ({ revision, helpOpen, setHelpOpen }), [revision, helpOpen]);

  return React.createElement(
    RegistryCtx.Provider, { value: registry },
    React.createElement(HelpCtx.Provider, { value: help }, children),
  );
}

/**
 * Bind `combo` to `handler` for as long as the calling component is mounted.
 *
 * @param opts.label        already-translated label for the `?` overlay; omit to stay unlisted
 * @param opts.group        overlay section (already translated)
 * @param opts.enabled      false unbinds without unmounting
 * @param opts.firesInInput override the caret rule
 */
export function useShortcut(combo, handler, opts = {}) {
  const { label, group, enabled = true, firesInInput } = opts;
  const registry = useContext(RegistryCtx);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = Symbol('shortcut');

  useEffect(() => {
    if (!registry || !enabled) return undefined;
    const normalized = normalizeCombo(combo);
    if (!normalized) return undefined;
    return registry.register(idRef.current, {
      combo: normalized, label, group, firesInInput,
      seq: ++SEQ,
      run: (e) => handlerRef.current?.(e),
    });
  }, [registry, combo, label, group, enabled, firesInInput]);
}

/** Everything currently bound and labelled, grouped for the overlay. */
export function useShortcutList() {
  const registry = useContext(RegistryCtx);
  const help = useContext(HelpCtx);
  const revision = help?.revision;
  return useMemo(() => {
    if (!registry) return [];
    const groups = new Map();
    for (const def of registry.regs.current.values()) {
      if (!def.label) continue;
      const g = def.group || '';
      if (!groups.has(g)) groups.set(g, new Map());
      const byCombo = groups.get(g);
      const prev = byCombo.get(def.combo);
      if (!prev || def.seq > prev.seq) byCombo.set(def.combo, def);
    }
    return [...groups.entries()].map(([group, byCombo]) => ({
      group,
      items: [...byCombo.values()].sort((a, b) => a.combo.localeCompare(b.combo)),
    }));
    // `revision` is the signal that the ref changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, revision]);
}

export function useShortcutHelp() {
  const help = useContext(HelpCtx);
  return {
    open: Boolean(help?.helpOpen),
    setOpen: useCallback((next) => help?.setHelpOpen(next), [help]),
    toggle: useCallback(() => help?.setHelpOpen((v) => !v), [help]),
  };
}

// ── A tiny app-wide "close the top sheet" bus for the global Esc binding ──
//
// Kit overlays close on Escape by themselves (Radix Dialog, BottomSheet). The
// app's own sheets and drawers register a closer here so the ONE global `esc`
// binding in App.jsx can close whichever is on top, instead of each screen
// adding its own window listener.

const closers = [];
export function registerEscapeCloser(fn) {
  closers.push(fn);
  return () => { const i = closers.indexOf(fn); if (i !== -1) closers.splice(i, 1); };
}
/** Close the most recently registered overlay. True when one was closed. */
export function closeTopOverlay() {
  for (let i = closers.length - 1; i >= 0; i -= 1) {
    if (closers[i]?.() === true) return true;
  }
  return false;
}
export function useEscapeCloser(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    return registerEscapeCloser(() => { onClose?.(); return true; });
  }, [open, onClose]);
}
